<template>
  <div class="tasks">
    <OmxList
        :data="filteredTasksData"
        :loading="isLoading"
        :search-value.sync="search"
        :empty-icon="hasSearch ? 'filter-variant' : 'format-list-checks'"
        :empty-title="hasSearch ? $t('omx-list.empty-title') : $t('tasks-view-empty.title')"
        :empty-body="hasSearch ? null : $t('tasks-view-empty.body')"
        :skeleton-columns="[1.6, 1.2, 0.8, 1, 1, 0.8, 0.8, 0.8, 1, 1]"
    >
      <template #actions>
        <OmxPermissionButton
            :allowed="canCreate"
            type="is-primary"
            icon-left="plus-thick"
            @click="addNewTaskClicked"
        >{{ $t('tasks-view.new-task-button') }}</OmxPermissionButton>
        <OmxPermissionButton
            :allowed="canCreate"
            icon-left="import"
            @click="importTasksClicked"
        >{{ $t('tasks-view.import-task-button') }}</OmxPermissionButton>
      </template>

      <template #empty-actions>
        <OmxPermissionButton
            v-if="!hasSearch"
            :allowed="canCreate"
            type="is-primary"
            icon-left="plus-thick"
            @click="addNewTaskClicked"
        >{{ $t('tasks-view.new-task-button') }}</OmxPermissionButton>
        <b-button v-if="hasSearch" @click="search = ''">{{ $t('omx-list.clear-selection') }}</b-button>
      </template>

      <b-table-column field="task.title" :label="$t('tasks-view.task-title-column')" v-slot="props" sortable>
        <router-link
            :to="{ name: 'TaskDetails', params: { project_id: project_id, task_id: props.row.task.document_id, is_template: is_template }}"
            class="omx-row-title"
        >{{ getAdjustedTitle(props.row.task.title) }}</router-link>
      </b-table-column>

      <b-table-column field="task.category" :label="$t('tasks-view.task-category-column')" v-slot="props" sortable>
        {{ props.row.task.category }}
      </b-table-column>

      <b-table-column field="quantity" :label="$t('tasks-view.task-quantity-column')" v-slot="props">
        {{ getFormattedQuantity(props.row.estimation, props.row.task.unit) }}
      </b-table-column>

      <b-table-column field="estimation.expected_fee_per_unit"
                      :label="$t('tasks-view.task-expected-fee-per-unit-column')" v-slot="props" sortable>
        {{ getFormattedPricing(props.row.estimation.expected_fee_per_unit) }}
      </b-table-column>

      <b-table-column field="estimation.expected_total_fee"
                      :label="$t('tasks-view.task-expected-total-fee-column')" v-slot="props" sortable>
        {{ getFormattedPricing(props.row.estimation.expected_total_fee) }}
      </b-table-column>

      <b-table-column field="performance" :label="$t('tasks-view.task-performance-column')" v-slot="props">
        {{ getFormattedPerformance(props.row.estimation.performance, props.row.task.unit) }}
      </b-table-column>

      <b-table-column field="estimation.number_of_internal_personnel"
                      :label="$t('tasks-view.task-internal-personnel-number-column')" v-slot="props" sortable>
        {{ props.row.estimation.number_of_internal_personnel }}
      </b-table-column>

      <b-table-column field="estimation.number_of_external_personnel"
                      :label="$t('tasks-view.task-external-personnel-number-column')" v-slot="props" sortable>
        {{ props.row.estimation.number_of_external_personnel }}
      </b-table-column>

      <b-table-column field="estimation.expected_internal_hours"
                      :label="$t('tasks-view.task-expected-internal-hours-column')" v-slot="props" sortable>
        {{ getFormattedTimeHour(props.row.estimation.expected_internal_hours) }}
      </b-table-column>

      <b-table-column field="estimation.expected_external_hours"
                      :label="$t('tasks-view.task-expected-external-hours-column')" v-slot="props" sortable>
        {{ getFormattedTimeHour(props.row.estimation.expected_external_hours) }}
      </b-table-column>
    </OmxList>

    <b-modal :width="640" v-model="isTaskDetailsModalActiveLocal" trap-focus :destroy-on-hide="true" aria-role="dialog" aria-modal>
      <template #default="props">
        <TaskDetails :project_id="project_id" task_id="new" @close="props.close" @taskAdded="readTasksData" />
      </template>
    </b-modal>
    <b-modal :width="640" v-model="isImportFromFileModalActiveLocal" trap-focus :destroy-on-hide="true" aria-role="dialog" aria-modal>
      <template #default="props">
        <ImportFromFileDetails :project_id="project_id" @close="props.close" @fileUploaded="onImportFileIsUploaded" />
      </template>
    </b-modal>
  </div>
</template>

<script>
import TaskDetails from "@/components/TaskDetails";
import firebase from "firebase/app";
import ImportFromFileDetails from "@/components/ImportFromFileDetails";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "Tasks",
  components: { OmxList, ImportFromFileDetails, TaskDetails },
  props: {
    project_id: { type: String },
    is_template: { type: Boolean }
  },
  data() {
    return {
      tasksData: [],
      isLoading: false,
      isTaskDetailsModalActiveLocal: false,
      isImportFromFileModalActiveLocal: false,
      search: ""
    };
  },
  computed: {
    canCreate() {
      return (!this.is_template && this.currentUserAccess('TaskDetails', this.UserActions.CREATE, this.project_id))
          || (this.is_template && this.currentUserAccess('ProjectTemplates', this.UserActions.CREATE));
    },
    hasSearch() {
      return this.search.trim() !== "";
    },
    filteredTasksData() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.tasksData;
      return this.tasksData.filter(row => {
        const t = row.task || {};
        return [t.title, t.category].filter(Boolean).join(" ").toLowerCase().includes(q);
      });
    }
  },
  methods: {
    readTasksData() {
      this.isTaskDetailsModalActiveLocal = false;
      this.isLoading = true;
      this.tasksData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getTasksFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getTasksFunction({ action: "get_tasks", project_id: this.project_id, fill_meta: true })
          .then((result) => {
            if (result.data.tasks.length > 0) {
              result.data["tasks"].forEach((task) => {
                this.tasksData.push({
                  task,
                  estimation: task.meta.estimation ? task.meta.estimation : {}
                });
              });
            }
            this.isLoading = false;
            this.$emit("tasks-loaded", this.tasksData.length);
          })
          .catch((error) => {
            this.showError(error.message);
            console.log("Error getting tasks:", error?.code);
            this.isLoading = false;
          });
    },
    onImportFileIsUploaded(filePath) {
      this.isImportFromFileModalActiveLocal = false;
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const importBulkTasksFunction = functions.httpsCallable(this.getSubmitEndpoint());
      importBulkTasksFunction({
        project_id: this.project_id,
        filename: filePath,
        action: "insert_bulk_tasks"
      })
          .then(() => {
            this.showSuccess(this.$t("tasks-view.tasks-imported-successfully"));
            this.readTasksData();
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(this.$t('messages.save-error'));
            console.error("import bulk tasks:", error?.code);
          });
    },
    addNewTaskClicked() { this.isTaskDetailsModalActiveLocal = true; },
    importTasksClicked() { this.isImportFromFileModalActiveLocal = true; }
  },
  mounted() {
    this.readTasksData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.tasks { margin-top: 16px; }
.omx-row-title {
  color: $omx-brand-600;
  font-weight: 600;
  &:hover { color: $omx-brand-700; text-decoration: underline; }
}
</style>
